/**
 * StoreTrackingPage — iOS + Android downloads & ratings.
 *
 * Two tabs (iOS / Android), each backed by `storeMetricsApi.getDownloads`
 * and `storeMetricsApi.getRatings` filtered by platform. The app list is
 * a DataGrid with sort / filter / search / CSV export / column-toggle.
 * Click a row to expand a country-breakdown drawer below the chart.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip as MuiTooltip,
  Typography,
  Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarIcon from '@mui/icons-material/Star';

import { storeMetricsApi, refreshCache } from '../services/mrdsApi';
import {
  PageHeader,
  MetricCard,
  SectionCard,
  EmptyState,
  DataTable,
  CompareChart,
} from '../components/ui';
import { PLATFORM_COLORS } from '../theme';

const PLATFORMS = [
  { key: 'ios', label: 'iOS' },
  { key: 'android', label: 'Android' },
];

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR'));

const buildRatingMap = (ratings) => {
  const map = new Map();
  ratings.forEach((r) => {
    map.set(r.app_platform_id, r);
  });
  return map;
};

export default function StoreTrackingPage() {
  const theme = useTheme();
  const [tab, setTab] = useState('ios');
  const [downloads, setDownloads] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedAppPlatformId, setSelectedAppPlatformId] = useState(null);
  const [compareIds, setCompareIds] = useState([]); // app_platform_id'ler (karsilastirma icin secili)
  const COMPARE_MAX = 10;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setSelectedAppPlatformId(null);
    setCompareIds([]);
    Promise.all([
      storeMetricsApi.getDownloads({ platform: tab }),
      storeMetricsApi.getRatings({ platform: tab }),
    ])
      .then(([dl, ra]) => {
        if (cancelled) return;
        setDownloads(dl);
        setRatings(ra);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError('Veriler yüklenirken hata oluştu.');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tab, reloadKey]);

  const ratingMap = useMemo(() => buildRatingMap(ratings), [ratings]);

  // Combine downloads + ratings into a single grid row
  const gridRows = useMemo(() => {
    return downloads.map((d) => {
      const rating = ratingMap.get(d.app_platform_id);
      return {
        id: d.app_platform_id,
        app_name: d.app_name,
        app_extid: d.app_extid,
        total_downloads: d.total_downloads,
        country_count: d.by_country.length,
        avg_rating: rating?.avg_rating ?? null,
        latest_rating: rating?.latest_rating ?? null,
        total_ratings: rating?.total_ratings ?? null,
        latest_date: rating?.latest_date ?? null,
        // raw passthroughs used by the row-detail panel
        _by_country: d.by_country,
      };
    });
  }, [downloads, ratingMap]);

  const totals = useMemo(() => {
    return gridRows.reduce(
      (acc, r) => ({
        apps: acc.apps + 1,
        total_downloads: acc.total_downloads + (r.total_downloads || 0),
      }),
      { apps: 0, total_downloads: 0 },
    );
  }, [gridRows]);

  const avgRating = useMemo(() => {
    const vals = ratings.map((r) => r.avg_rating).filter((v) => v != null);
    if (!vals.length) return null;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2);
  }, [ratings]);

  const platformLabel = PLATFORMS.find((p) => p.key === tab)?.label || tab;
  const accent = PLATFORM_COLORS[tab] || theme.palette.primary.main;

  const selectedRow = gridRows.find((r) => r.id === selectedAppPlatformId);

  // ---- DataGrid columns ------------------------------------------------
  const columns = [
    {
      field: 'app_name',
      headerName: 'App',
      flex: 1.4,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'app_extid',
      headerName: tab === 'ios' ? 'App Store ID' : 'Package',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{ fontFamily: 'monospace' }}
        />
      ),
    },
    {
      field: 'total_downloads',
      headerName: 'Downloads',
      type: 'number',
      flex: 0.9,
      minWidth: 120,
      valueFormatter: ({ value }) => fmt(value),
    },
    {
      field: 'country_count',
      headerName: 'Countries',
      type: 'number',
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: 'avg_rating',
      headerName: 'Avg ★',
      type: 'number',
      flex: 0.6,
      minWidth: 100,
      renderCell: ({ value }) =>
        value == null ? '—' : (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <StarIcon sx={{ fontSize: 14, color: '#fbbf24' }} />
            <Typography variant="body2">{Number(value).toFixed(2)}</Typography>
          </Stack>
        ),
    },
    {
      field: 'total_ratings',
      headerName: 'Ratings',
      type: 'number',
      flex: 0.7,
      minWidth: 110,
      valueFormatter: ({ value }) => fmt(value),
    },
    {
      field: 'latest_date',
      headerName: 'As of',
      flex: 0.8,
      minWidth: 110,
      renderCell: ({ value }) =>
        value ? (
          <Typography variant="caption" color="text.secondary">
            {value}
          </Typography>
        ) : '—',
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Store Tracking"
        subtitle="Cumulative downloads and average rating per app, broken down by country. Tick rows to compare on a chosen metric."
        rightSlot={
          <MuiTooltip title="Refresh">
            <IconButton
              onClick={() => {
                refreshCache();
                setReloadKey((k) => k + 1);
              }}
            >
              <RefreshIcon />
            </IconButton>
          </MuiTooltip>
        }
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2 }}
        textColor="primary"
        indicatorColor="primary"
      >
        {PLATFORMS.map((p) => (
          <Tab key={p.key} value={p.key} label={p.label} />
        ))}
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <MetricCard label={`${platformLabel} apps`} value={fmt(totals.apps)} accent={accent} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            label="Total downloads"
            value={fmt(totals.total_downloads)}
            accent={PLATFORM_COLORS.android}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="Apps with rating" value={fmt(ratings.length)} accent="#D97706" />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            label="Avg rating"
            value={avgRating == null ? '—' : avgRating}
            accent="#9333EA"
          />
        </Grid>
      </Grid>

      <SectionCard
        title="Apps"
        description={`Tick up to ${COMPARE_MAX} rows to compare them, or click a row to see its country breakdown.`}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : gridRows.length === 0 ? (
          <EmptyState title={`No download rows yet for ${platformLabel}.`} dense />
        ) : (
          <DataTable
            rows={gridRows}
            columns={columns}
            loading={loading}
            getRowId={(r) => r.id}
            height={520}
            onRowClick={(p) =>
              setSelectedAppPlatformId((prev) => (prev === p.id ? null : p.id))
            }
            initialState={{
              sorting: { sortModel: [{ field: 'total_downloads', sort: 'desc' }] },
            }}
            sx={{
              '& .MuiDataGrid-row': { cursor: 'pointer' },
            }}
            checkboxSelection
            rowSelectionModel={compareIds}
            onRowSelectionModelChange={(ids) => {
              if (ids.length > COMPARE_MAX) {
                // Keep the most-recent COMPARE_MAX picks
                setCompareIds(ids.slice(-COMPARE_MAX));
              } else {
                setCompareIds(ids);
              }
            }}
          />
        )}
      </SectionCard>

      {/* Compare card -- shows up only when at least 2 rows are ticked */}
      {compareIds.length >= 2 && (
        <Box sx={{ mt: 2 }}>
          <CompareChart
            title="Compare apps"
            description={`Pick a metric to plot the ticked apps side by side. ${platformLabel} only.`}
            rows={gridRows.filter((r) => compareIds.includes(r.id))}
            labelField="app_name"
            metrics={[
              { field: 'total_downloads', label: 'Total downloads' },
              { field: 'avg_rating',      label: 'Average rating',
                format: (v) => v == null ? '—' : Number(v).toFixed(2) },
              { field: 'latest_rating',   label: 'Latest rating',
                format: (v) => v == null ? '—' : Number(v).toFixed(2) },
              { field: 'total_ratings',   label: 'Total ratings' },
              { field: 'country_count',   label: 'Country count' },
            ]}
            rightSlot={
              <Chip
                label={`${compareIds.length} / ${COMPARE_MAX}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            }
          />
        </Box>
      )}

      {/* Drill-down panel */}
      {selectedRow && (
        <SectionCard
          title={`${selectedRow.app_name} — country breakdown`}
          description={`${selectedRow._by_country.length} countries`}
          sx={{ mt: 2 }}
        >
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Country</TableCell>
                  <TableCell align="right">Downloads</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedRow._by_country.map((c, i) => (
                  <TableRow key={`${c.country}-${i}`}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{c.country}</Typography>
                        <Chip label={c.currency} size="small" variant="outlined" />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{fmt(c.downloads)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}
    </Container>
  );
}
